import { useState } from 'react';

import { Button } from '@breezeventures/ui-kit/button';
import { Modal } from '@breezeventures/ui-kit/modal';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { RadioGroup } from '@breezeventures/ui-kit/radio-group';
import { Typography } from '@breezeventures/ui-kit/typography';

export type MobileSelectOption<TValue extends string = string> = {
  value: TValue;
  label: string;
};

type Props<TValue extends string = string> = {
  options: MobileSelectOption<TValue>[];
  value?: TValue;
  onChange: (value: TValue) => void;
  title?: string;
  placeholder?: string;
  triggerPrefix?: string;
};

export function MobileSelect<TValue extends string = string>({
  options,
  value,
  onChange,
  title,
  placeholder = 'Select',
  triggerPrefix,
}: Props<TValue>) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<TValue | undefined>(value);

  const selectedLabel = value
    ? (options.find((o) => o.value === value)?.label ?? placeholder)
    : placeholder;

  const triggerLabel = triggerPrefix
    ? `${triggerPrefix} ${selectedLabel}`
    : selectedLabel;

  const open = () => {
    setDraft(value);
    setOpened(true);
  };

  const apply = () => {
    if (draft !== undefined) {
      onChange(draft);
    }
    setOpened(false);
  };

  return (
    <>
      <Button type="button" size="s" variant="secondary" onClick={open}>
        {triggerLabel}
        <Icon
          name="chevron-down"
          size={24}
          version="v1"
          className={`size-4 shrink-0`}
        />
      </Button>

      <Modal opened={opened} onClose={() => setOpened(false)}>
        {title && (
          <Typography.Text
            size="m"
            weight="medium"
            tag="p"
            className="text-center"
          >
            {title}
          </Typography.Text>
        )}

        <RadioGroup.Root
          value={draft ?? ''}
          onChange={(next) => setDraft(next as TValue)}
          className={`
            mt-4 divide-y divide-separator overflow-hidden rounded-2xl border
            border-separator
          `}
        >
          {options.map((option) => (
            <RadioGroup.Item
              key={option.value}
              value={option.value}
              className="p-4"
            >
              {option.label}
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>

        <div className="mt-6">
          <Modal.FooterActions>
            <Button
              variant="primary"
              size="l"
              width="full"
              disabled={draft === undefined}
              onClick={apply}
            >
              Apply
            </Button>
          </Modal.FooterActions>
        </div>
      </Modal>
    </>
  );
}
